import { ActionContext, ActionTree } from 'vuex';
import ArchivApiService from '@/service/ArchivApiService';

import { RootState } from '@/store';

import { State } from './state';

import { ActionTypes as CommonActionTypes } from '@/store/modules/Common/actions';
import { Mutations, MutationTypes } from './mutations';
import { AuthUser, LoginRequest, ExternalLoginRequest, LoginConfig, LogoutRequest, RefreshLoginRequest } from '@/models/AuthModels';
import { ErrorResponseData } from '@/models/CommonModels';
import { ApiKey, CreateApiKeyRequest, CreateApiKeyResponse } from '@/models/ApiKeyModels';

export enum ActionTypes {
  Login = 'LOGIN',
  Logout = 'LOGOUT',
  LoginAzure = 'LOGINAZURE',
  LoginKeycloak = 'LOGINKEYCLOAK',
  ValidateOTP = 'VALIDATEOTP',
  RefreshLogin = 'REFRESHLOGIN',
  GetLoginConfig = 'GETLOGINCONFIG',

  GetAllApiKeys = 'GETALLAPIKEYS',
  CreateApiKey = 'CREATEAPIKEY',
  ResetCreatedApiKey = 'RESETCREATEDAPIKEY',
  DeleteApiKey = 'DELETEAPIKEY',
}

type ActionArguments = Omit<ActionContext<State, RootState>, 'commit'> & {
  commit<K extends keyof Mutations>(key: K, payload: Parameters<Mutations[K]>[1]): ReturnType<Mutations[K]>;
};

export type Actions = {
  [ActionTypes.Login](context: ActionArguments, data: LoginRequest): Promise<void>;
  [ActionTypes.Logout](context: ActionArguments): Promise<void>;
  [ActionTypes.LoginAzure](context: ActionArguments, azureLogin: ExternalLoginRequest): Promise<void>;
  [ActionTypes.LoginKeycloak](context: ActionArguments, keycloakLogin: ExternalLoginRequest): Promise<void>;
  [ActionTypes.ValidateOTP](context: ActionArguments, data: LoginRequest): Promise<void>;
  [ActionTypes.RefreshLogin](context: ActionArguments): Promise<void>;
  [ActionTypes.GetLoginConfig](context: ActionArguments): Promise<void>;

  [ActionTypes.GetAllApiKeys](context: ActionArguments): Promise<void>;
  [ActionTypes.CreateApiKey](context: ActionArguments, data: CreateApiKeyRequest): Promise<void>;
  [ActionTypes.ResetCreatedApiKey](context: ActionArguments): void;
  [ActionTypes.DeleteApiKey](context: ActionArguments, id: number): Promise<void>;
};

export const actions: ActionTree<State, RootState> & Actions = {
  async [ActionTypes.Login]({ commit, dispatch, rootGetters }: ActionArguments, data: LoginRequest) {
    const status = rootGetters.status;
    status.authLoading = true;
    status.authErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ArchivApiService.login(data)
      .then((res) => {
        const user: AuthUser = res.data;
        commit(MutationTypes.LoginSuccess, user);
      })
      .catch((error) => {
        var errorresponse = (error.response?.data as ErrorResponseData) || null;
        status.authErrorMsg = errorresponse.title;
        localStorage.removeItem('authUser');
      })
      .finally(() => {
        status.authLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.Logout]({ commit, dispatch, rootGetters }: ActionArguments) {
    const status = rootGetters.status;
    status.authLoading = true;
    status.authErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ArchivApiService.logout(new LogoutRequest(rootGetters.authUser.refreshToken))
      .then(() => {
        console.log('LOGOUT');
      })
      .catch((error) => {
        console.log('LOGOUT CATCH');
        console.log(error);
        // status.authLoading = false;
      })
      .finally(() => {
        dispatch(CommonActionTypes.SetStatus, status);
        sessionStorage.clear();
        let secureBrowserToken = localStorage.getItem('SECBROWSTOKEN');
        let archivSortBy = localStorage.getItem('archivSortBy');

        localStorage.clear();
        if (archivSortBy) {
          localStorage.setItem('archivSortBy', archivSortBy);
        }

        if (secureBrowserToken != null) {
          localStorage.setItem('SECBROWSTOKEN', secureBrowserToken);
        }

        status.authLoading = false;
        commit(MutationTypes.Logout, undefined);
      });
  },

  async [ActionTypes.LoginAzure]({ commit, dispatch, rootGetters }: ActionArguments, azureLogin: ExternalLoginRequest) {
    const status = rootGetters.status;
    status.authLoading = true;
    status.authErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ArchivApiService.loginAzure(azureLogin.idToken)
      .then((res) => {
        const user: AuthUser = res.data;
        commit(MutationTypes.LoginSuccess, user);
      })
      .catch((error) => {
        status.authErrorMsg = error.response.data.title;
        localStorage.removeItem('authUser');
      })
      .finally(() => {
        status.authLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.LoginKeycloak]({ commit, dispatch, rootGetters }: ActionArguments, keycloakLogin: ExternalLoginRequest) {
    const status = rootGetters.status;
    status.authLoading = true;
    status.authErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ArchivApiService.loginKeycloak(keycloakLogin.idToken)
      .then((res) => {
        const user: AuthUser = res.data;
        commit(MutationTypes.LoginSuccess, user);
      })
      .catch((error) => {
        status.authErrorMsg = error.response.data.title;
        localStorage.removeItem('authUser');
      })
      .finally(() => {
        status.authLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.ValidateOTP]({ commit, dispatch, rootGetters }: ActionArguments, data: LoginRequest) {
    const status = rootGetters.status;
    status.authLoading = true;
    status.authErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ArchivApiService.validateOTPLogin(data)
      .then((res) => {
        const user: AuthUser = res.data;
        commit(MutationTypes.LoginSuccess, user);
      })
      .catch((error) => {
        var errorresponse = (error.response?.data as ErrorResponseData) || null;
        status.authErrorMsg = errorresponse.title;
        localStorage.removeItem('authUser');
      })
      .finally(() => {
        status.authLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.RefreshLogin]({ commit, rootGetters }: ActionArguments) {
    const request = new RefreshLoginRequest(rootGetters.authUser.refreshToken);
    const res = await ArchivApiService.refreshLogin(request);

    const result: AuthUser = res.data;
    commit(MutationTypes.RefreshLoginSuccess, result);

    return;
  },

  async [ActionTypes.GetLoginConfig]({ commit }: ActionArguments): Promise<void> {
    return ArchivApiService.getLoginConfig()
      .then((res) => {
        const data: LoginConfig = res.data;
        commit(MutationTypes.SetLoginConfig, data);
      })
      .catch(() => {
        localStorage.removeItem('loginconfig');
        // console.error(error)
      });
  },

  //#region ApiKeys
  async [ActionTypes.GetAllApiKeys]({ commit, dispatch, rootGetters }: ActionArguments): Promise<void> {
    const status = rootGetters.status;
    status.getApiKeysLoading = true;
    status.apiKeysErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ArchivApiService.getAllApiKeys()
      .then((res) => {
        const apiKeys: Array<ApiKey> = res.data;
        commit(MutationTypes.SetApiKeys, apiKeys);
      })
      .catch((error) => {
        var errorresponse = (error.response?.data as ErrorResponseData) || null;
        status.apiKeysErrorMsg = errorresponse.title;
      })
      .finally(() => {
        status.getApiKeysLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.CreateApiKey]({ commit, dispatch, rootGetters }: ActionArguments, data: CreateApiKeyRequest): Promise<void> {
    const status = rootGetters.status;
    status.createApiKeysLoading = true;
    status.apiKeysErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ArchivApiService.createApiKey(data)
      .then((res) => {
        const createdApiKey: CreateApiKeyResponse = res.data;
        commit(MutationTypes.SetCreatedApiKey, createdApiKey);

        if (createdApiKey != null) {
          const newKey: ApiKey = {
            id: createdApiKey.id,
            name: createdApiKey.name,
            description: createdApiKey.description,
            createdAt: createdApiKey.createdAt,
            lastUsedAt: createdApiKey.lastUsedAt,
            expiresAt: createdApiKey.expiresAt,
            isActive: createdApiKey.isActive,
            createdBy: createdApiKey.createdBy,
          };

          commit(MutationTypes.UpsertApiKey, newKey);
        }
      })
      .catch((error) => {
        var errorresponse = (error.response?.data as ErrorResponseData) || null;
        status.apiKeysErrorMsg = errorresponse.title;
      })
      .finally(() => {
        status.createApiKeysLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  [ActionTypes.ResetCreatedApiKey]({ commit }: ActionArguments): void {
    commit(MutationTypes.ResetCreatedApiKey, undefined);
  },

  async [ActionTypes.DeleteApiKey]({ commit, dispatch, rootGetters }: ActionArguments, id: number): Promise<void> {
    const status = rootGetters.status;
    status.deleteApiKeysLoading = true;
    status.apiKeysErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ArchivApiService.deleteApiKey(id)
      .then(() => {
        commit(MutationTypes.RemoveApiKey, id);
      })
      .catch((error) => {
        var errorresponse = (error.response?.data as ErrorResponseData) || null;
        status.apiKeysErrorMsg = errorresponse.title;
      })
      .finally(() => {
        status.deleteApiKeysLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },
  //#endregion
};
